import queryString from 'query-string';
import {autobind} from 'core-decorators';
import {getDOMNode, getDOMNodes} from 'utils';
import BaseAdapter from 'adapters/base';
import {parseOptions} from './options';

// http://player.kaltura.com/docs/api

@autobind
export default class KalturaAdapter extends BaseAdapter {
    static findVideos() {
        const nodes = getDOMNodes('script[src*="cdnapisec.kaltura.com/"], script[src*="cdnapi.kaltura.com/"]');
        return nodes.map(script => {
            const playerNode = getDOMNode('.kWidgetIframeContainer', script.parentNode);
            const qs = queryString.parse(script.src.substr(script.src.indexOf('?')));

            const playerNodeId = qs.playerId || (playerNode && playerNode.getAttribute('id'));

            if (!playerNodeId) return undefined;

            return {
                provider: 'kaltura',
                embedType: 'OnScreen JS Embed',
                videoId: qs.entry_id,
                entryNode: script,
                playerNodeId,
            };
        }).filter(Boolean);
    }

    getOptions() {
        return parseOptions(this.api);
    }

    initialize(options) {
        this.videoNode = options.videoNode;
        this.playerNodeId = options.playerNodeId;
        this.apiNode = options.apiNode;
        return super.initialize(options);
    }

    getEventBinder(api) {
        if (api.kBind) return api.kBind;
        if (api.addJsListener) return api.addJsListener;
    }

    addEventListener(event, callbak) {
        const binder = this.getEventBinder(this.api);
        if (binder) binder(event, callbak);
    }

    loadAPI() {
        return new Promise((resolve, reject) => {
            if (this.apiNode && this.videoNode && this.getEventBinder(this.apiNode)) {
                return resolve();
            }

            const check = () => {
                const videoNode = getDOMNode(`#${this.playerNodeId}.kWidgetIframeContainer`);
                if (window.kWidget && videoNode && this.getEventBinder(videoNode)) {
                    this.apiNode = videoNode;
                    this.videoNode = videoNode;
                    resolve();
                } else {
                    setTimeout(check, BaseAdapter.CHECK_INTERVAL);
                }
            };
            check();

            const timeout = () => reject('Kaltura Player API failed to load');
            setTimeout(timeout, BaseAdapter.API_TIMEOUT);
        });
    }

    attach() {
        return new Promise((resolve, reject) => {
            this.api = this.apiNode; // Kaltura's API is exposed on the DOM node
            this.addEventListener('playerUpdatePlayhead', this.syncPlayhead);
            this.addEventListener('playerStateChange', this.handleStateChange);
            this.addEventListener('playerError', () => reject('Kaltura Player initialization failed'));

            const check = () => {
                if (this.getDuration() !== 0) {
                    this.handleReady();
                    resolve();
                } else {
                    setTimeout(check, BaseAdapter.CHECK_INTERVAL);
                }
            };
            check();
        });
    }

    handleReady() {
        this.setDuration(this.getDuration());
    }

    handleStateChange(state) {
        this.isPlaying = state === 'playing';

        if (this.isPlaying) {
            this.seeking = false;

            if (!this.disabledCaptions) {
                this.disabledCaptions = true;
                this.api.sendNotification('hideClosedCaptions');
            }
        }

        const stateNames = {
            buffering: BaseAdapter.STATES.Buffering,
            paused: BaseAdapter.STATES.Paused,
            playing: BaseAdapter.STATES.Playing,
        };

        if (stateNames[state]) this.setPlayerState(stateNames[state]);
    }

    syncPlayhead(currentTime) {
        if (!this.seeking) this.setPlayhead(this.toMS(currentTime));
    }

    seek(time) {
        this.seeking = true;
        this.api.sendNotification('doSeek', this.toSS(time));
    }

    pause() {
        this.api.sendNotification('doPause');
    }

    getDuration() {
        return this.toMS(this.api.evaluate('{duration}'));
    }

    getCurrentTime() {
        return this.toMS(this.api.evaluate('{video.player.currentTime}'));
    }

    getVideoTitle() {
        return Promise.resolve(this.api.evaluate('{mediaProxy.entry.name}'));
    }

    getMetadata() {
        const {
            name,
            createdAt,
            description,
            thumbnailUrl,
            dataUrl: contentUrl,
        } = this.api.evaluate('{mediaProxy.entry}');

        return {
            name,
            contentUrl,
            description,
            thumbnailUrl,
            uploadDate: new Date(createdAt).toISOString(),
        };
    }
}



// WEBPACK FOOTER //
// ./scripts/adapters/kaltura/index.js